/**
 * Accounts Page
 * Displays all user accounts grouped by type with balance summary
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccounts, useAccountsMeta, useBalanceSummary, useSyncAccounts, useSetAccountVisibility, groupAccountsByType } from '@/hooks/useAccounts';
import { AccountList, AccountListSkeleton, AccountSummary, AccountSummarySkeleton, ManualAccountModal, CloseAccountModal, DeleteAccountModal, EditAccountNameModal, BalanceHistoryModal, AccountHoldingsModal, MergeAccountDialog, DuplicateAccountsBanner } from '@/components/accounts';
import type { AccountWithConnection } from '@/services/accounts';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';

export default function AccountsPage() {
  const [showHidden, setShowHidden] = useState(false);
  const [showManualAccountModal, setShowManualAccountModal] = useState(false);
  const [accountToClose, setAccountToClose] = useState<AccountWithConnection | null>(null);
  // WP-FLOW-7: State for edit account name modal (connected accounts)
  const [accountToEdit, setAccountToEdit] = useState<AccountWithConnection | null>(null);
  // State for editing manual accounts (uses ManualAccountModal for full editing)
  const [manualAccountToEdit, setManualAccountToEdit] = useState<AccountWithConnection | null>(null);
  // WP-FLOW-10: State for balance history modal
  const [accountForHistory, setAccountForHistory] = useState<AccountWithConnection | null>(null);
  // WP-MANUAL-HOLD-2: State for holdings modal
  const [accountForHoldings, setAccountForHoldings] = useState<AccountWithConnection | null>(null);
  // Account merge state. mergeTarget is set only by the duplicates banner,
  // which knows which account the source duplicates (WP-BUG-63).
  const [mergeSource, setMergeSource] = useState<AccountWithConnection | null>(null);
  const [mergeTarget, setMergeTarget] = useState<AccountWithConnection | null>(null);
  // WP-ACCT-2: State for delete account modal (orphaned accounts)
  const [accountToDelete, setAccountToDelete] = useState<AccountWithConnection | null>(null);

  // Navigation for connecting accounts
  const navigate = useNavigate();

  const handleConnectAccount = useCallback(() => {
    navigate('/connections');
  }, [navigate]);

  // Fetch accounts data
  // WP-BUG-62: includeHidden is required here. This page filters hidden
  // accounts client-side (see filteredAccounts below) and offers a "Show
  // hidden" toggle, but the backend excludes hidden accounts by default - so
  // without this flag the list never contained one, hiddenCount was always 0,
  // the toggle never rendered, and a hidden account could never be recovered.
  const {
    data: accounts = [],
    isLoading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useAccounts({ includeHidden: true });

  // WP-BUG-62: hiddenCount comes from the backend rather than from counting
  // the fetched page, so it stays correct beyond the list's page limit.
  // Shares a cache entry with useAccounts above - no extra request.
  const { data: accountsMeta } = useAccountsMeta({ includeHidden: true });

  // Fetch balance summary
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useBalanceSummary();

  // Sync mutation
  const {
    mutate: syncAccounts,
    isPending: isSyncing,
  } = useSyncAccounts();

  // Filter and group accounts
  const filteredAccounts = showHidden
    ? accounts
    : accounts.filter((acc) => !acc.isHidden);
  const groupedAccounts = groupAccountsByType(filteredAccounts);

  // Count hidden accounts (backend-authoritative, falls back to the fetched page)
  const hiddenCount =
    accountsMeta?.hiddenCount ?? accounts.filter((acc) => acc.isHidden).length;

  // WP-BUG-62: unhide (or re-hide) an account from the accounts list
  const { mutate: setAccountVisibility } = useSetAccountVisibility();

  const handleToggleVisibility = useCallback(
    (account: AccountWithConnection) => {
      setAccountVisibility({ account, isHidden: !account.isHidden });
    },
    [setAccountVisibility]
  );

  // Handle sync
  const handleSync = () => {
    syncAccounts(undefined, {
      onSuccess: () => {
        refetchAccounts();
      },
    });
  };

  const hasError = accountsError || summaryError;
  const isLoading = accountsLoading || summaryLoading;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accounts</h1>
          <p className="mt-1 text-gray-600">
            Manage your connected financial accounts
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Sync button -- always visible (primary action) */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            isLoading={isSyncing}
            disabled={accounts.length === 0}
            leftIcon={<SyncIcon className="h-4 w-4" />}
          >
            Sync Now
          </Button>

          {/* Desktop: show all buttons inline */}
          <div className="hidden sm:flex items-center gap-3">
            {hiddenCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHidden(!showHidden)}
                aria-pressed={showHidden}
              >
                {showHidden ? 'Hide hidden' : `Show hidden (${hiddenCount})`}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowManualAccountModal(true)}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Add Manual Account
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/connections')}
            >
              Manage Connections
            </Button>
          </div>

          {/* Mobile: dropdown for secondary actions (WP-WEBMOB-2) */}
          <div className="sm:hidden">
            <AccountActionsDropdown
              showHidden={showHidden}
              onToggleHidden={() => setShowHidden(!showHidden)}
              hiddenCount={hiddenCount}
              onAddManual={() => setShowManualAccountModal(true)}
              onManageConnections={() => navigate('/connections')}
            />
          </div>
        </div>
      </div>

      {/* Manual Account Modal - for creating new */}
      <ManualAccountModal
        isOpen={showManualAccountModal}
        onClose={() => setShowManualAccountModal(false)}
      />

      {/* Manual Account Modal - for editing existing */}
      <ManualAccountModal
        isOpen={manualAccountToEdit !== null}
        onClose={() => setManualAccountToEdit(null)}
        account={manualAccountToEdit ?? undefined}
      />

      {/* Close Account Modal */}
      <CloseAccountModal
        isOpen={accountToClose !== null}
        onClose={() => setAccountToClose(null)}
        account={accountToClose}
      />

      {/* WP-FLOW-7: Edit Account Name Modal */}
      <EditAccountNameModal
        isOpen={accountToEdit !== null}
        onClose={() => setAccountToEdit(null)}
        account={accountToEdit}
      />

      {/* WP-FLOW-10: Balance History Modal */}
      <BalanceHistoryModal
        isOpen={accountForHistory !== null}
        onClose={() => setAccountForHistory(null)}
        account={accountForHistory}
      />

      {/* WP-MANUAL-HOLD-2: Account Holdings Modal */}
      <AccountHoldingsModal
        isOpen={accountForHoldings !== null}
        onClose={() => setAccountForHoldings(null)}
        account={accountForHoldings}
      />

      {/* Merge Account Dialog */}
      <MergeAccountDialog
        isOpen={mergeSource !== null}
        onClose={() => {
          setMergeSource(null);
          setMergeTarget(null);
        }}
        sourceAccount={mergeSource}
        suggestedTarget={mergeTarget}
      />

      {/* WP-ACCT-2: Delete Account Modal (orphaned accounts) */}
      <DeleteAccountModal
        isOpen={accountToDelete !== null}
        onClose={() => setAccountToDelete(null)}
        account={accountToDelete}
      />

      {/* Error state */}
      {hasError && (
        <Alert
          variant="error"
          title="Failed to load accounts"
          className="mb-6"
          onDismiss={() => refetchAccounts()}
        >
          There was a problem loading your accounts. Please try again.
        </Alert>
      )}

      {/* WP-BUG-63: Surface accounts duplicated by an institution re-link.
          Renders nothing when there are none. */}
      <DuplicateAccountsBanner
        accounts={accounts}
        onMergeClick={(source, target) => {
          setMergeTarget(target);
          setMergeSource(source);
        }}
        className="mb-6"
      />

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Account list */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          <Card>
            {isLoading ? (
              <AccountListSkeleton groups={3} />
            ) : accounts.length === 0 ? (
              <EmptyAccountsState onConnectAccount={handleConnectAccount} />
            ) : (
              <AccountList
                groupedAccounts={groupedAccounts}
                showTotals
                onCloseClick={setAccountToClose}
                onEditClick={(account) => {
                  // Route to appropriate modal based on account type
                  // Manual accounts (no connectionId) use ManualAccountModal for full editing
                  // Connected accounts use EditAccountNameModal for name-only editing
                  if (!account.connectionId) {
                    setManualAccountToEdit(account);
                  } else {
                    setAccountToEdit(account);
                  }
                }}
                onBalanceHistoryClick={setAccountForHistory}
                onHoldingsClick={setAccountForHoldings}
                onMergeClick={setMergeSource}
                onDeleteClick={setAccountToDelete}
                onToggleVisibilityClick={handleToggleVisibility}
              />
            )}
          </Card>
        </div>

        {/* Right column: Summary */}
        <div className="lg:col-span-1 order-1 lg:order-2">
          {summaryLoading ? (
            <AccountSummarySkeleton />
          ) : summary ? (
            <AccountSummary summary={summary} />
          ) : (
            <Card>
              <div className="text-center py-8 text-gray-500">
                <p>Unable to load summary</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Empty state when no accounts are connected
 */
function EmptyAccountsState({ onConnectAccount }: { onConnectAccount: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
        <BankIcon className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="mt-4 text-lg font-medium text-gray-900">
        No accounts connected
      </h3>
      <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
        Connect your bank accounts to see your balances and track your spending across all your financial institutions.
      </p>
      <div className="mt-6">
        <Button
          variant="primary"
          leftIcon={<PlusIcon className="h-5 w-5" />}
          onClick={onConnectAccount}
        >
          Connect Account
        </Button>
      </div>
    </div>
  );
}

function SyncIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function BankIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"
      />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.5v15m7.5-7.5h-15"
      />
    </svg>
  );
}

/**
 * WP-WEBMOB-2: Mobile-only dropdown for account page secondary actions.
 * Uses same dropdown pattern as TenantSwitcher (absolute positioned, click-outside-to-close).
 */
interface AccountActionsDropdownProps {
  showHidden: boolean;
  onToggleHidden: () => void;
  hiddenCount: number;
  onAddManual: () => void;
  onManageConnections: () => void;
}

function AccountActionsDropdown({
  showHidden,
  onToggleHidden,
  hiddenCount,
  onAddManual,
  onManageConnections,
}: AccountActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        aria-label="More actions"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
          role="menu"
        >
          <div className="py-1">
            <button
              type="button"
              onClick={() => handleAction(onAddManual)}
              className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
              role="menuitem"
            >
              <PlusIcon className="h-4 w-4 mr-3 text-gray-400" />
              Add Manual Account
            </button>
            <button
              type="button"
              onClick={() => handleAction(onManageConnections)}
              className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
              role="menuitem"
            >
              <svg className="h-4 w-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.686-3.374a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364l1.757 1.757" />
              </svg>
              Manage Connections
            </button>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => handleAction(onToggleHidden)}
                className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                role="menuitem"
              >
                <svg className="h-4 w-4 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={showHidden ? "M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" : "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178zM15 12a3 3 0 11-6 0 3 3 0 016 0z"} />
                </svg>
                {showHidden ? 'Hide hidden' : `Show hidden (${hiddenCount})`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
